import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { AlertCircle, ArrowLeft, Home } from 'lucide-react';
import { Button } from './ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/Card';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'user' | 'admin' | 'superadmin';
  requiredRoles?: string[];
  requireOrganization?: boolean;
  organizationId?: string;
  fallback?: React.ReactNode;
  excludeUsers?: string[]; // List of user emails to exclude from this route
}

// Helper function to check if user has required role with proper hierarchy
function hasRequiredRole(userRoles: string[] | undefined, requiredRole: string): boolean {
  if (!userRoles || userRoles.length === 0) return false;
  
  // Exact match
  if (userRoles.includes(requiredRole)) return true;
  
  // Role hierarchy: superadmin > admin > user
  if (userRoles.includes('superadmin')) {
    return requiredRole === 'admin' || requiredRole === 'user';
  }
  
  if (userRoles.includes('admin')) {
    return requiredRole === 'user';
  }
  
  return false;
}

// Helper function to check if user has any of the required roles
function hasAnyRequiredRole(userRoles: string[] | undefined, requiredRoles: string[]): boolean {
  if (!userRoles || userRoles.length === 0) return false;
  
  // Check for superadmin first (has access to everything)
  if (userRoles.includes('superadmin')) return true;
  
  // Check if user has any of the required roles
  return requiredRoles.some(role => hasRequiredRole(userRoles, role));
}

// Helper function to check organization access
function hasOrganizationAccess(user: {role?: string; orgId?: string}, organizationId?: string): boolean {
  if (!user) return false;
  
  // Superadmin has access to all organizations
  if (user.roles?.includes('superadmin')) return true;
  
  // If no specific organization required, just check if user has an organization
  if (!organizationId) {
    return !!user.orgId;
  }
  
  // Check if user belongs to the required organization
  return user.orgId === organizationId;
}

// Error component for unauthorized access
const UnauthorizedError: React.FC<{
  title: string;
  message: string;
  showBackButton?: boolean;
  showHomeButton?: boolean;
}> = ({ title, message, showBackButton = true, showHomeButton = true }) => {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <AlertCircle className="w-6 h-6 text-red-600" />
          </div>
          <CardTitle className="text-red-900">{title}</CardTitle>
          <CardDescription className="text-red-700">
            {message}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {showBackButton && location.key !== 'default' && (
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              className="w-full"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Back
            </Button>
          )}
          {showHomeButton && (
            <Button
              onClick={() => navigate('/dashboard')}
              className="w-full"
            >
              <Home className="w-4 h-4 mr-2" />
              Go to Dashboard
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Loading component
const LoadingSpinner: React.FC<{ message?: string }> = ({ message = "Loading..." }) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin mx-auto mb-4" />
      <p className="text-muted-foreground">{message}</p>
    </div>
  </div>
);

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole,
  requiredRoles,
  requireOrganization = false,
  organizationId,
  fallback,
  excludeUsers = []
}) => {
  const { isAuthenticated, user, isLoading, error } = useAuth();
  const navigate = useNavigate();

  // Show loading state
  if (isLoading) {
    return <LoadingSpinner message="Checking permissions..." />;
  }

  // Handle authentication errors
  if (error) {
    return (
      <UnauthorizedError
        title="Authentication Error"
        message="There was an issue with your authentication. Please try logging in again."
        showBackButton={false}
        showHomeButton={false}
      />
    );
  }

  // Check if user is authenticated
  if (!isAuthenticated || !user) {
    // Store current location for redirect after login
    const currentPath = window.location.pathname + window.location.search;
    if (currentPath !== '/login' && currentPath !== '/') {
      try {
        sessionStorage.setItem('auth_redirect_url', currentPath);
      } catch (e) {
        console.warn('Could not store redirect URL:', e);
      }
    }
    
    // Redirect to login
    navigate('/login', { replace: true });
    return <LoadingSpinner message="Redirecting to login..." />;
  }

  // Check if user is excluded from this route
  if (excludeUsers.length > 0 && user.email && excludeUsers.includes(user.email)) {
    if (fallback) {
      return <>{fallback}</>;
    }
    
    return (
      <UnauthorizedError
        title="Access Restricted"
        message="This page is not available for your account. Please contact support if you believe this is an error."
      />
    );
  }

  // Check role requirements
  if (requiredRole && !hasRequiredRole(user.roles, requiredRole)) {
    const roleDisplayNames = {
      'user': 'User',
      'admin': 'Administrator', 
      'superadmin': 'Super Administrator'
    };
    
    if (fallback) {
      return <>{fallback}</>;
    }
    
    return (
      <UnauthorizedError
        title="Access Denied"
        message={`This page requires ${roleDisplayNames[requiredRole as keyof typeof roleDisplayNames] || requiredRole} privileges. Your current role: ${user.role || 'Unknown'}.`}
      />
    );
  }

  // Check multiple role requirements
  if (requiredRoles && requiredRoles.length > 0 && !hasAnyRequiredRole(user.roles, requiredRoles)) {
    if (fallback) {
      return <>{fallback}</>;
    }
    
    return (
      <UnauthorizedError
        title="Access Denied"
        message={`This page requires one of the following roles: ${requiredRoles.join(', ')}. Your current role: ${user.role || 'Unknown'}.`}
      />
    );
  }

  // Check organization requirements
  if (requireOrganization && !hasOrganizationAccess(user, organizationId)) {
    if (fallback) {
      return <>{fallback}</>;
    }
    
    let message = "This page requires organization access.";
    if (!user.orgId) {
      message = "You must be a member of an organization to access this page.";
    } else if (organizationId && user.orgId !== organizationId) {
      message = "You don't have access to this organization's resources.";
    }
    
    return (
      <UnauthorizedError
        title="Organization Access Required"
        message={message}
      />
    );
  }

  // All checks passed, render children
  return <>{children}</>;
};

// Convenience components for common protection patterns
export const AdminRoute: React.FC<{ children: React.ReactNode; fallback?: React.ReactNode }> = ({ 
  children, 
  fallback 
}) => (
  <ProtectedRoute requiredRole="admin" fallback={fallback}>
    {children}
  </ProtectedRoute>
);

export const SuperAdminRoute: React.FC<{ children: React.ReactNode; fallback?: React.ReactNode }> = ({ 
  children, 
  fallback 
}) => (
  <ProtectedRoute requiredRole="superadmin" fallback={fallback}>
    {children}
  </ProtectedRoute>
);

export const OrganizationRoute: React.FC<{ 
  children: React.ReactNode; 
  organizationId?: string;
  fallback?: React.ReactNode;
}> = ({ children, organizationId, fallback }) => (
  <ProtectedRoute requireOrganization organizationId={organizationId} fallback={fallback}>
    {children}
  </ProtectedRoute>
);

export default ProtectedRoute; 